import { useCallback, useEffect, useRef } from "react";
import { useAuthProviders } from "../contexts/AuthContext";
import { useHandleCreateSession } from "../hooks/useHandleCreateSession";
import { initializeGoogleSignin } from "./GoogleAuthSdk";

export function useInitializeGoogleSignin() {
  const initializeRef = useRef(false);
  const handleCreateSession = useHandleCreateSession("google");
  const { setGoogleLoading } = useAuthProviders();

  const init = useCallback(() => {
    setGoogleLoading(false);
    initializeGoogleSignin((token) => {
      handleCreateSession(token);
      setGoogleLoading(false);
    });
    window.removeEventListener("GoogleSignInLoaded", init);
    initializeRef.current = true;
  }, [setGoogleLoading, handleCreateSession]);

  // Initialize google accounts. We need to call it once per page as that is what google recommends.
  // See: https://developers.google.com/identity/gsi/web/reference/js-reference#google.accounts.id.initialize
  useEffect(() => {
    if (initializeRef.current) return;
    // Google suggests window.onload, but ads delay it. If for whatever reason it's fast, we may also miss attaching to window.onload.
    // So, we have a custom event that is fired onload for the script, which we can use to initialize Google Sign-In.
    if (window.googleSignInLoaded) {
      init();
    } else {
      window.addEventListener("GoogleSignInLoaded", () => {
        init();
      });
    }

    return () => {
      window.removeEventListener("GoogleSignInLoaded", init);
    };
  }, [init]);
}
